'use client';

import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { Link } from '@/i18n/routing';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useHoverPrefetch } from '@/shared/hooks/use-hover-prefetch';
import { cn } from '@ecom/helper-react-class-combine';

type NavMenuLinkProps = {
  href: string;
  className?: string;
  isMainNav?: boolean;
  ariaLabel?: string;
  prefetch?: boolean;
  prefetchOnHover?: boolean;
  ariaCurrentValue?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
  separator?: 'left' | 'right' | 'both' | 'none';
  onClick?: () => void;
  unlocalized?: boolean;
};

export const NavMenuLink = forwardRef<
  HTMLAnchorElement,
  Omit<ComponentPropsWithoutRef<'a'>, 'popover'> & NavMenuLinkProps
>(
  (
    {
      href,
      children,
      className = '',
      isMainNav = false,
      prefetch,
      prefetchOnHover = false,
      ariaLabel,
      ariaCurrentValue,
      onClick,
      onMouseEnter,
      unlocalized = false,
      separator,
      ...rest
    },
    ref,
  ) => {
    const hoverPrefetch = useHoverPrefetch({ enabled: prefetchOnHover, prefetch, onMouseEnter });
    const linkClassName = cn(
      'group font-body-bold hover:bg-background-subtle relative transition-colors',
      'focus-visible:ring-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1',
      isMainNav ? 'flex h-[54px] items-center px-4' : 'block px-2 py-2',
      className,
    );

    const content = (
      <span className='relative'>
        {children}

        {/* Bottom highlight */}
        {separator != 'none' && (
          <span className='bg-primary absolute -bottom-1 left-0 h-1 w-full opacity-0 transition-opacity group-hover:opacity-100 data-[active]:opacity-100' />
        )}

        {/* Left separator */}
        {(separator === 'left' || separator === 'both') && (
          <span className='pointer-events-none absolute top-1/2 -left-4 h-7 w-px -translate-y-1/2 bg-white' />
        )}

        {/* Right separator */}
        {(separator === 'right' || separator === 'both') && (
          <span className='pointer-events-none absolute top-1/2 -right-4 h-7 w-px -translate-y-1/2 bg-white' />
        )}
      </span>
    );

    return (
      <NavigationMenu.Link asChild>
        {unlocalized ? (
          <a
            href={href}
            ref={ref}
            aria-label={ariaLabel}
            aria-current={ariaCurrentValue}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            className={linkClassName}
            {...rest}
          >
            {content}
          </a>
        ) : (
          <Link
            href={href}
            ref={ref}
            aria-label={ariaLabel}
            aria-current={ariaCurrentValue}
            onClick={onClick}
            onMouseEnter={hoverPrefetch.onMouseEnter}
            prefetch={hoverPrefetch.prefetch}
            className={linkClassName}
            {...rest}
          >
            {content}
          </Link>
        )}
      </NavigationMenu.Link>
    );
  },
);

NavMenuLink.displayName = 'NavMenuLink';
